import { useState, useEffect, useCallback } from "react"
import {
  UserCheck,
  UserX,
  AlertTriangle,
  Users,
  User as UserIcon,
  Eye,
  UserPlus,
  Edit,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Building,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  Key,
  Copy,
  CheckCircle,
} from "lucide-react"
import type { User, Agent } from "@/features/auth"
import type { Team } from "@/features/organizations"
import { HRService } from "@/features/hr"
import { AgentShimmerTable } from "@/shared/components"
import { formatPhone } from "@/shared/utils"

interface LicensedAgentStatusManagerProps {
  onError: (error: string) => void
}

interface FilterState {
  teamId?: string
  status?: string
  emLevel?: string
  dmLevel?: string
  cmLevel?: string
  bmLevel?: string
  search?: string
}

interface NewAgentData {
  email: string
  firstName: string
  lastName: string
  phone?: string
  startDate: string
  address?: string
  emergencyContact?: string
  emergencyPhone?: string
  agentId?: string
  managerName?: string
  managerNameSource?: string
  managerEmail?: string
  managerEmailSource?: string
}

export default function LicensedAgentStatusManager({ onError }: LicensedAgentStatusManagerProps) {
  const [agents, setAgents] = useState<Agent[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [organizations, setOrganizations] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [teamsLoading, setTeamsLoading] = useState(false)
  const [organizationsLoading, setOrganizationsLoading] = useState(false)
  const [filters, setFilters] = useState<FilterState>({})
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showAddAgentModal, setShowAddAgentModal] = useState(false)
  const [showAgentDetailsModal, setShowAgentDetailsModal] = useState(false)
  const [showEditAgentModal, setShowEditAgentModal] = useState(false)
  const [actionType, setActionType] = useState<"activate" | "deactivate" | "terminate" | null>(null)
  const [addingAgent, setAddingAgent] = useState(false)
  const [updatingAgent, setUpdatingAgent] = useState(false)
  const [agentCreationError, setAgentCreationError] = useState<string>("")
  const [agentUpdateError, setAgentUpdateError] = useState<string>("")
  const [statusChangeLoading, setStatusChangeLoading] = useState(false)
  const [loadingAgentId, setLoadingAgentId] = useState<string | null>(null)

  // Password reset state
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
  const [selectedAgentForReset, setSelectedAgentForReset] = useState<any>(null)
  const [resettingPassword, setResettingPassword] = useState(false)
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false)
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null)
  const [newTemporaryPassword, setNewTemporaryPassword] = useState<string>("")
  const [passwordCopied, setPasswordCopied] = useState(false)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Sorting state
  const [sortField, setSortField] = useState<string>("createdAt")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc")

  // Define sortable fields
  type SortableField = "agentName" | "status" | "team" | "createdAt"

  const [newAgentData, setNewAgentData] = useState<NewAgentData>({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    startDate: new Date().toISOString().split("T")[0],
    address: "",
    emergencyContact: "",
    emergencyPhone: "",
    agentId: "",
    managerNameSource: "auto",
    managerEmailSource: "auto",
  })

  const [editAgentData, setEditAgentData] = useState<NewAgentData>({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    startDate: new Date().toISOString().split("T")[0],
    address: "",
    emergencyContact: "",
    emergencyPhone: "",
    managerNameSource: "auto",
    managerEmailSource: "auto",
  })
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null)

  // Load teams from backend
  const loadTeams = async () => {
    try {
      setTeamsLoading(true)
      const result = await HRService.getTeams()
      setTeams(result || [])
    } catch (error) {
      console.error("Error loading teams:", error)
      onError("Failed to load teams")
    } finally {
      setTeamsLoading(false)
    }
  }

  // Load organizations from backend
  const loadOrganizations = async () => {
    try {
      setOrganizationsLoading(true)
      const result = await HRService.getOrganizations()
      setOrganizations(Array.isArray(result) ? result : [])
    } catch (error) {
      console.error("Error loading organizations:", error)
      onError("Failed to load organizations")
      setOrganizations([]) // Ensure organizations is always an array
    } finally {
      setOrganizationsLoading(false)
    }
  }


  // Load agents from backend
  const loadAgents = async () => {
    try {
      setLoading(true)
      // Convert frontend filters to backend format
      const backendFilters = {
        teamId: filters.teamId,
        status: filters.status === "ACTIVE" ? "active" : filters.status === "DEACTIVATED" ? "inactive" : filters.status === "FIRED" ? "terminated" : undefined,
        search: filters.search,
        emLevel: filters.emLevel,
        dmLevel: filters.dmLevel,
        cmLevel: filters.cmLevel,
        bmLevel: filters.bmLevel,
      }
      
      const result = await HRService.getAgents(backendFilters)
      setAgents(result || [])
    } catch (error) {
      console.error("Error loading agents:", error)
      onError("Failed to load agents")
    } finally {
      setLoading(false)
    }
  }

  // Load teams, organizations, and agents on component mount
  useEffect(() => {
    loadTeams()
    loadOrganizations()
    loadAgents()
  }, [])

  // Load agents when filters change
  useEffect(() => {
    loadAgents()
  }, [filters])

  // Agents are now filtered on the backend, so we use them directly
  const filteredAgents = agents

  // Sort filtered agents
  const sortedAgents = [...filteredAgents].sort((a, b) => {
    let aValue: any
    let bValue: any

    switch (sortField) {
      case "agentName":
        aValue = `${a.user?.firstName || ''} ${a.user?.lastName || ''}`.toLowerCase()
        bValue = `${b.user?.firstName || ''} ${b.user?.lastName || ''}`.toLowerCase()
        break
      case "status":
        aValue = a.isActive ? 'ACTIVE' : 'INACTIVE'
        bValue = b.isActive ? 'ACTIVE' : 'INACTIVE'
        break
      case "team":
        aValue = teams.find((t) => t.id === a.teamId)?.name || ''
        bValue = teams.find((t) => t.id === b.teamId)?.name || ''
        break
      case "createdAt":
        aValue = new Date(a.createdAt).getTime()
        bValue = new Date(b.createdAt).getTime()
        break
      default:
        return 0
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1
    return 0
  })

  // Pagination calculations
  const totalPages = Math.ceil(sortedAgents.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedAgents = sortedAgents.slice(startIndex, endIndex)

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filters.teamId, filters.status, filters.search, filters.emLevel, filters.dmLevel, filters.cmLevel, filters.bmLevel])

  // Handle sorting
  const handleSort = (field: SortableField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  // Get sort icon
  const getSortIcon = (field: SortableField) => {
    if (sortField !== field) return null
    return sortDirection === "asc" ? "↑" : "↓"
  }


  const handleStatusChange = async (agent: Agent, newStatus: "activate" | "deactivate" | "terminate") => {
    setSelectedAgent(agent)
    setActionType(newStatus)
    setShowConfirmModal(true)
  }

  const confirmStatusChange = async () => {
    if (!selectedAgent || !actionType) return

    setStatusChangeLoading(true)
    setLoadingAgentId(selectedAgent.id)
    
    try {
      
      // Call the real API to update agent status
      const result = await HRService.updateAgentStatus(selectedAgent.id, actionType)
      

      // Reload agents to show the updated status
      await loadAgents()

      setShowConfirmModal(false)
      setSelectedAgent(null)
      setActionType(null)
    } catch (error: any) {
      console.error("Error updating agent status:", error)
      
      // Extract error message from different possible error structures
      let errorMessage = "Failed to update licensed agent status"
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error?.message) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      
      onError(errorMessage)
    } finally {
      setStatusChangeLoading(false)
      setLoadingAgentId(null)
    }
  }

  const handleAddAgent = async () => {
    if (!newAgentData.email || !newAgentData.firstName || !newAgentData.lastName) {
      setAgentCreationError("Please fill in all required fields")
      return
    }

    setAddingAgent(true)
    setAgentCreationError("") // Clear any previous errors
    try {
      
      // Call the real API to create agent
      const result = await HRService.createAgent({
        email: newAgentData.email,
        firstName: newAgentData.firstName,
        lastName: newAgentData.lastName,
        phone: newAgentData.phone,
        agentId: newAgentData.agentId || undefined,
      })

      // Reload agents to show the new one
      await loadAgents()

      // Reset form and close modal
      setNewAgentData({
        email: "",
        firstName: "",
        lastName: "",
        phone: "",
        startDate: new Date().toISOString().split("T")[0],
        address: "",
        emergencyContact: "",
        emergencyPhone: "",
        agentId: "",
      })
      setShowAddAgentModal(false)
      setAgentCreationError("") // Clear any errors
    } catch (error: any) {
      console.error("Error creating agent:", error)
      
      // Extract error message from different possible error structures
      let errorMessage = "Failed to add licensed agent"
      
      if (error?.response?.data?.message) {
        // API error response
        errorMessage = error.response.data.message
      } else if (error?.response?.data?.error) {
        // Alternative API error structure
        errorMessage = error.response.data.error
      } else if (error?.message) {
        // Standard Error object
        errorMessage = error.message
      } else if (typeof error === 'string') {
        // String error
        errorMessage = error
      }
      
      setAgentCreationError(errorMessage)
    } finally {
      setAddingAgent(false)
    }
  }

  const handleViewAgentDetails = (agent: Agent) => {
    setSelectedAgent(agent)
    setShowAgentDetailsModal(true)
  }

  const handleEditAgent = (agent: Agent) => {
    setEditingAgent(agent)
    // Access optional properties safely - these may not exist on the User type
    const agentUser = agent.user as any
    setEditAgentData({
      email: agent.user.email || "",
      firstName: agent.user.firstName || "",
      lastName: agent.user.lastName || "",
      phone: agentUser?.phone || "",
      startDate: agentUser?.startDate ? new Date(agentUser.startDate).toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
      address: agentUser?.address || "",
      emergencyContact: agentUser?.emergencyContact || "",
      emergencyPhone: agentUser?.emergencyPhone || "",
      managerName: agent.managerName || "",
      managerNameSource: agent.managerNameSource || "auto",
      managerEmail: agent.managerEmail || "",
      managerEmailSource: agent.managerEmailSource || "auto",
    })
    setShowEditAgentModal(true)
    setShowAgentDetailsModal(false) // Close details modal if open
  }

  const handleUpdateAgent = async () => {
    if (!editingAgent || !editAgentData.email || !editAgentData.firstName || !editAgentData.lastName) {
      setAgentUpdateError("Please fill in all required fields")
      return
    }

    setUpdatingAgent(true)
    setAgentUpdateError("")
    
    try {
      await HRService.updateAgent(editingAgent.id, {
        email: editAgentData.email,
        firstName: editAgentData.firstName,
        lastName: editAgentData.lastName,
        phone: editAgentData.phone,
        managerName: editAgentData.managerNameSource === "manual" ? editAgentData.managerName : undefined,
        managerNameSource: editAgentData.managerNameSource,
        managerEmail: editAgentData.managerEmailSource === "manual" ? editAgentData.managerEmail : undefined,
        managerEmailSource: editAgentData.managerEmailSource,
      })

      // Reload agents to show the updated data
      await loadAgents()

      // Reset form and close modal
      setShowEditAgentModal(false)
      setEditingAgent(null)
      setEditAgentData({
        email: "",
        firstName: "",
        lastName: "",
        phone: "",
        startDate: new Date().toISOString().split("T")[0],
        address: "",
        emergencyContact: "",
        emergencyPhone: "",
      })
      setAgentUpdateError("")
    } catch (error: any) {
      console.error("Error updating agent:", error)
      
      // Extract error message from different possible error structures
      let errorMessage = "Failed to update licensed agent"
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error?.message) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      
      setAgentUpdateError(errorMessage)
    } finally {
      setUpdatingAgent(false)
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items)
    setCurrentPage(1)
  }

  const handleResetPassword = async (agentId: string) => {
    try {
      setResettingPassword(true)
      setResetPasswordError(null)
      
      // Call backend API to reset password
      const response = await HRService.resetAgentPassword(agentId)
      
      // Store the new temporary password to show to HR manager
      if (response.data) {
        setNewTemporaryPassword(response.data.temporaryPassword)
        setResetPasswordSuccess(true)
      }
      
    } catch (error: any) {
      console.error('Error resetting password:', error)
      const errorMessage = error?.response?.data?.message || error.message || 'Failed to reset password'
      setResetPasswordError(errorMessage)
    } finally {
      setResettingPassword(false)
    }
  }

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(newTemporaryPassword)
    setPasswordCopied(true)
    setTimeout(() => setPasswordCopied(false), 2000)
  }

  const getStatusBadge = (agent: Agent) => {
    const baseClasses = "inline-flex px-2 py-1 text-xs font-semibold rounded-full"
    
    switch (agent.status) {
      case "active":
        return `${baseClasses} bg-green-100 text-green-800`
      case "deactivated":
        return `${baseClasses} bg-orange-100 text-orange-800`
      case "terminated":
        return `${baseClasses} bg-red-100 text-red-800`
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`
    }
  }

  const getStatusText = (agent: Agent) => {
    switch (agent.status) {
      case "active":
        return "ACTIVE"
      case "deactivated":
        return "DEACTIVATED"
      case "terminated":
        return "TERMINATED"
      default:
        return "UNKNOWN"
    }
  }

  const getStatusIcon = (agent: Agent) => {
    switch (agent.status) {
      case "active":
        return <UserCheck className="w-4 h-4 text-green-600" />
      case "deactivated":
        return <UserX className="w-4 h-4 text-orange-600" />
      case "terminated":
        return <AlertTriangle className="w-4 h-4 text-red-600" />
      default:
        return <UserIcon className="w-4 h-4 text-gray-600" />
    }
  }

  const getActionText = () => {
    if (!actionType || !selectedAgent) return ""

    switch (actionType) {
      case "activate":
        return `activate ${selectedAgent.user.email}`
      case "deactivate":
        return `deactivate ${selectedAgent.user.email}`
      case "terminate":
        return `permanently terminate ${selectedAgent.user.email}`
      default:
        return ""
    }
  }

  // Pagination component
  const PaginationControls = () => {
    const getPageNumbers = () => {
      const pages = []
      const maxVisiblePages = 5

      if (totalPages <= maxVisiblePages) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= 4; i++) {
            pages.push(i)
          }
          pages.push("...")
          pages.push(totalPages)
        } else if (currentPage >= totalPages - 2) {
          pages.push(1)
          pages.push("...")
          for (let i = totalPages - 3; i <= totalPages; i++) {
            pages.push(i)
          }
        } else {
          pages.push(1)
          pages.push("...")
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pages.push(i)
          }
          pages.push("...")
          pages.push(totalPages)
        }
      }

      return pages
    }

    if (totalPages <= 1) return null

    return (
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 pt-4 border-t border-accent-200">
        <div className="flex items-center space-x-2 text-sm text-accent-600">
          <span>Show</span>
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="border border-accent-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span>per page</span>
        </div>

        <div className="flex items-center space-x-2 text-sm text-accent-600">
          <span>
            Showing {startIndex + 1} to {Math.min(endIndex, filteredAgents.length)} of {filteredAgents.length} results
          </span>
        </div>

        <div className="flex items-center space-x-1">
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-accent-300 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="First page"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>

          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-accent-300 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex items-center space-x-1">
            {getPageNumbers().map((page, index) => (
              <button
                key={index}
                onClick={() => (typeof page === "number" ? handlePageChange(page) : undefined)}
                disabled={page === "..."}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  page === currentPage
                    ? "bg-primary-600 text-white"
                    : page === "..."
                      ? "cursor-default text-accent-400"
                      : "border border-accent-300 hover:bg-accent-50 text-accent-700"
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-accent-300 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-accent-300 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Last page"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-accent-900">Licensed Agent Status</h2>
          <p className="text-accent-600 mt-1">Manage licensed agents, create new accounts, and control access</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowAddAgentModal(true)}
            className="btn-primary flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700"
          >
            <UserPlus className="w-4 h-4" />
            <span>Add New Licensed Agent</span>
          </button>
        </div>
      </div>

      {/* Licensed Agent Status Overview */}
      <div className="card">
        <h3 className="text-lg font-semibold text-accent-900 mb-4">Licensed Agent Status Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {agents.filter((a) => a.isActive).length}
            </div>
            <div className="text-sm text-green-700">Active</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {agents.filter((a) => !a.isActive).length}
            </div>
            <div className="text-sm text-orange-700">Suspended</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{agents.length}</div>
            <div className="text-sm text-blue-700">Total Licensed Agents</div>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{agents.filter((a) => !a.isActive).length}</div>
            <div className="text-sm text-red-700">Terminated</div>
          </div>
        </div>
      </div>

      {/* Quick Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <div
          className="card cursor-pointer hover:shadow-lg transition-all duration-200 border-2 border-blue-200 hover:border-blue-400"
          onClick={() => setShowAddAgentModal(true)}
        >
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <UserPlus className="w-6 h-6 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-accent-900">Add New Licensed Agent</h3>
              <p className="text-sm text-accent-600">Create licensed agent account</p>
            </div>
          </div>
          <p className="text-xs text-accent-500 mb-3">Click here to add a new licensed agent to your organization.</p>
          <div className="text-right">
            <span className="text-blue-600 text-sm font-medium">Click to Add →</span>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-green-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-accent-900">Activate Licensed Agent</h3>
              <p className="text-sm text-accent-600">Restore licensed agent access</p>
            </div>
          </div>
          <p className="text-xs text-accent-500 mb-3">Reactivate suspended licensed agents and restore full system access.</p>
        </div>

        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <UserX className="w-5 h-5 text-orange-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-accent-900">Deactivate Licensed Agent</h3>
              <p className="text-sm text-accent-600">Temporarily suspend access</p>
            </div>
          </div>
          <p className="text-xs text-accent-500 mb-3">
            Suspend licensed agent access while preserving data and commission structure.
          </p>
        </div>

        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-accent-900">Terminate Licensed Agent</h3>
              <p className="text-sm text-accent-600">Permanently remove access</p>
            </div>
          </div>
          <p className="text-xs text-accent-500 mb-3">Permanently terminate licensed agent and remove all system access.</p>
        </div>

        {/* Reset Password */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Key className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-accent-900">Reset Password</h3>
              <p className="text-sm text-accent-600">Reset licensed agent's password</p>
            </div>
          </div>
          <p className="text-xs text-accent-500 mb-3">Generate a new temporary password for a licensed agent and send it via email.</p>
        </div>
      </div>

      {/* Filter */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {/* Search Bar */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-1">Search Licensed Agent Code/Name</label>
            <input
              type="text"
              className="input-field"
                    placeholder="Search by Licensed Agent Code, Name, or Email..."
              value={filters.search || ""}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>

          {/* Team Filter */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-1">Team (AM)</label>
            <select
              className="input-field"
              value={filters.teamId || ""}
              onChange={(e) => setFilters({ ...filters, teamId: e.target.value })}
              disabled={teamsLoading}
            >
              <option value="">All Teams</option>
              {teamsLoading ? (
                <option disabled>Loading teams...</option>
              ) : (
                teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-1">Status</label>
            <select
              className="input-field"
              value={filters.status || ""}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="DEACTIVATED">Deactivated</option>
              <option value="FIRED">Terminated</option>
              <option value="PENDING">Pending</option>
            </select>
          </div>

          {/* EM Level Filter */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-1">EM Level</label>
            <select
              className="input-field"
              value={filters.emLevel || ""}
              onChange={(e) => setFilters({ ...filters, emLevel: e.target.value })}
              disabled={organizationsLoading}
            >
              <option value="">All EM Organizations</option>
              {organizationsLoading ? (
                <option disabled>Loading...</option>
              ) : (
                organizations
                  .filter(org => org.type === "EM")
                  .map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))
              )}
            </select>
          </div>

          {/* DM Level Filter */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-1">DM Level</label>
            <select
              className="input-field"
              value={filters.dmLevel || ""}
              onChange={(e) => setFilters({ ...filters, dmLevel: e.target.value })}
              disabled={organizationsLoading}
            >
              <option value="">All DM Organizations</option>
              {organizationsLoading ? (
                <option disabled>Loading...</option>
              ) : (
                organizations
                  .filter(org => org.type === "DM")
                  .map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))
              )}
            </select>
          </div>

          {/* CM Level Filter */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-1">CM Level</label>
            <select
              className="input-field"
              value={filters.cmLevel || ""}
              onChange={(e) => setFilters({ ...filters, cmLevel: e.target.value })}
              disabled={organizationsLoading}
            >
              <option value="">All CM Organizations</option>
              {organizationsLoading ? (
                <option disabled>Loading...</option>
              ) : (
                organizations
                  .filter(org => org.type === "CM")
                  .map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))
              )}
            </select>
          </div>

          {/* BM Level Filter */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-1">BM Level</label>
            <select
              className="input-field"
              value={filters.bmLevel || ""}
              onChange={(e) => setFilters({ ...filters, bmLevel: e.target.value })}
              disabled={organizationsLoading}
            >
              <option value="">All BM Organizations</option>
              {organizationsLoading ? (
                <option disabled>Loading...</option>
              ) : (
                organizations
                  .filter(org => org.type === "BM")
                  .map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Agents Table */}
      <div className="card">
        {loading ? (
          <AgentShimmerTable rows={itemsPerPage} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-accent-200">
                <thead className="bg-accent-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("agentName")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Licensed Agent</span>
                        {getSortIcon("agentName")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {getSortIcon("status")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("team")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Team</span>
                        {getSortIcon("team")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("createdAt")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Created</span>
                        {getSortIcon("createdAt")}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-accent-200">
                  {paginatedAgents.map((agent) => (
                    <tr key={agent.id} className="hover:bg-accent-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-accent-900">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-primary-600 font-medium text-sm">
                              {agent.user.firstName?.[0] || agent.user.email[0].toUpperCase()}
                              {agent.user.lastName?.[0] || ""}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium">
                              {agent.user.firstName && agent.user.lastName ? `${agent.user.firstName} ${agent.user.lastName}` : agent.user.email}
                            </div>
                            <div className="text-xs text-accent-500">{agent.user.email}</div>
                            <div className="text-xs text-accent-400">ID: {agent.agentCode}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(agent)}
                          <span className={getStatusBadge(agent)}>{getStatusText(agent)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-500">
                        {teamsLoading ? (
                          <span className="text-accent-400">Loading...</span>
                        ) : (
                          teams.find((t) => t.id === agent.teamId)?.name || "Unassigned"
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-500">
                        {new Date(agent.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewAgentDetails(agent)}
                            className="text-accent-600 hover:text-accent-900 p-1 rounded"
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {agent.status !== "active" && (
                            <button
                              onClick={() => handleStatusChange(agent, "activate")}
                              disabled={loadingAgentId === agent.id}
                              className="text-green-600 hover:text-green-900 p-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Activate licensed agent"
                            >
                              {loadingAgentId === agent.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <UserCheck className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {agent.status === "active" && (
                            <button
                              onClick={() => handleStatusChange(agent, "deactivate")}
                              disabled={loadingAgentId === agent.id}
                              className="text-orange-600 hover:text-orange-900 p-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Deactivate licensed agent"
                            >
                              {loadingAgentId === agent.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <UserX className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {agent.status === "active" && (
                            <button
                              onClick={() => handleStatusChange(agent, "terminate")}
                              disabled={loadingAgentId === agent.id}
                              className="text-red-600 hover:text-red-900 p-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Terminate licensed agent"
                            >
                              {loadingAgentId === agent.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <AlertTriangle className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedAgentForReset(agent)
                              setShowResetPasswordModal(true)
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                            title="Reset password"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredAgents.length === 0 && !loading && (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-accent-300 mx-auto mb-4" />
                  <p className="text-accent-500">No licensed agents found matching your criteria.</p>
                  <button
                    onClick={() => setShowAddAgentModal(true)}
                    className="mt-4 btn-primary flex items-center space-x-2 mx-auto"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>Add Your First Licensed Agent</span>
                  </button>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            <PaginationControls />
          </>
        )}
      </div>

      {/* Add Agent Modal */}
      {showAddAgentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-accent-900">Add New Licensed Agent</h3>
                </div>
                <button
                  onClick={() => {
                    setShowAddAgentModal(false)
                    setAgentCreationError("")
                  }}
                  className="text-accent-400 hover:text-accent-600 p-1 rounded-lg hover:bg-accent-100"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Error Display */}
              {agentCreationError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                    <p className="text-red-800 font-medium">{agentCreationError}</p>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h4 className="text-lg font-medium text-accent-900 mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="input-field"
                        value={newAgentData.firstName}
                        onChange={(e) => setNewAgentData({ ...newAgentData, firstName: e.target.value })}
                        placeholder="Enter first name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="input-field"
                        value={newAgentData.lastName}
                        onChange={(e) => setNewAgentData({ ...newAgentData, lastName: e.target.value })}
                        placeholder="Enter last name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
                        <input
                          type="email"
                          className="input-field pl-10"
                          value={newAgentData.email}
                          onChange={(e) => setNewAgentData({ ...newAgentData, email: e.target.value })}
                          placeholder="salesrep@company.com"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">Phone Number</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
                        <input
                          type="tel"
                          className="input-field pl-10"
                          value={newAgentData.phone}
                          onChange={(e) => {
                            const formatted = formatPhone(e.target.value);
                            setNewAgentData({ ...newAgentData, phone: formatted });
                          }}
                          placeholder="(123) 456-7890"
                          maxLength={14}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">
                        Licensed Agent ID <span className="text-accent-500 text-xs font-normal">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        className="input-field"
                        value={newAgentData.agentId || ""}
                        onChange={(e) => setNewAgentData({ ...newAgentData, agentId: e.target.value })}
                        placeholder="Custom Licensed Agent ID"
                      />
                    </div>
                  </div>
                </div>

                {/* Manager Information */}
                <div>
                  <h4 className="text-lg font-medium text-accent-900 mb-4 flex items-center">
                    <UserIcon className="w-5 h-5 mr-2" />
                    Manager Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">Manager Email</label>
                      <select
                        className="input-field mb-2"
                        value={newAgentData.managerEmailSource}
                        onChange={(e) => setNewAgentData({ ...newAgentData, managerEmailSource: e.target.value })}
                      >
                        <option value="auto">Auto Inherit (Recruiter's Email)</option>
                        <option value="manual">Enter Manually</option>
                      </select>
                      {newAgentData.managerEmailSource === "manual" && (
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
                          <input
                            type="email"
                            className="input-field pl-10"
                            value={newAgentData.managerEmail}
                            onChange={(e) => setNewAgentData({ ...newAgentData, managerEmail: e.target.value })}
                            placeholder="manager@company.com"
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">Manager Name</label>
                      <select
                        className="input-field mb-2"
                        value={newAgentData.managerNameSource}
                        onChange={(e) => setNewAgentData({ ...newAgentData, managerNameSource: e.target.value })}
                      >
                        <option value="auto">Auto Inherit (Recruiter's Name)</option>
                        <option value="manual">Enter Manually</option>
                      </select>
                      {newAgentData.managerNameSource === "manual" && (
                        <div className="relative">
                          <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
                          <input
                            type="text"
                            className="input-field pl-10"
                            value={newAgentData.managerName}
                            onChange={(e) => setNewAgentData({ ...newAgentData, managerName: e.target.value })}
                            placeholder="Manager Full Name"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Work Information */}
                <div>
                  <h4 className="text-lg font-medium text-accent-900 mb-4 flex items-center">
                    <Building className="w-5 h-5 mr-2" />
                    Work Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">
                        Start Date <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
                        <input
                          type="date"
                          className="input-field pl-10"
                          value={newAgentData.startDate}
                          onChange={(e) => setNewAgentData({ ...newAgentData, startDate: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div>
                  <h4 className="text-lg font-medium text-accent-900 mb-4 flex items-center">
                    <MapPin className="w-5 h-5 mr-2" />
                    Additional Information
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">Address</label>
                      <textarea
                        className="input-field"
                        rows={2}
                        value={newAgentData.address}
                        onChange={(e) => setNewAgentData({ ...newAgentData, address: e.target.value })}
                        placeholder="Enter full address"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-accent-700 mb-2">Emergency Contact Name</label>
                        <input
                          type="text"
                          className="input-field"
                          value={newAgentData.emergencyContact}
                          onChange={(e) => setNewAgentData({ ...newAgentData, emergencyContact: e.target.value })}
                          placeholder="Contact person name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-accent-700 mb-2">
                          Emergency Contact Phone
                        </label>
                        <input
                          type="tel"
                          className="input-field"
                          value={newAgentData.emergencyPhone}
                          onChange={(e) => {
                            const formatted = formatPhone(e.target.value);
                            setNewAgentData({ ...newAgentData, emergencyPhone: formatted });
                          }}
                          placeholder="(123) 456-7890"
                          maxLength={14}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-accent-200">
                <button onClick={() => {
                  setShowAddAgentModal(false)
                  setAgentCreationError("")
                }} className="btn-outline" disabled={addingAgent}>
                  Cancel
                </button>
                <button
                  onClick={handleAddAgent}
                  disabled={addingAgent}
                  className="btn-primary flex items-center space-x-2 bg-blue-600 hover:bg-blue-700"
                >
                  {addingAgent ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Creating Licensed Agent...</span>
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      <span>Create Licensed Agent</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agent Details Modal */}
      {showAgentDetailsModal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-primary-600 font-medium text-lg">
                      {selectedAgent.user.firstName?.[0] || selectedAgent.user.email[0].toUpperCase()}
                      {selectedAgent.user.lastName?.[0] || ""}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-accent-900">
                      {selectedAgent.user.firstName && selectedAgent.user.lastName
                        ? `${selectedAgent.user.firstName} ${selectedAgent.user.lastName}`
                        : selectedAgent.user.email}
                    </h3>
                    <p className="text-accent-600">{selectedAgent.user.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAgentDetailsModal(false)}
                  className="text-accent-400 hover:text-accent-600 p-1 rounded-lg hover:bg-accent-100"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Status */}
                <div className="bg-accent-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    {getStatusIcon(selectedAgent)}
                    <span className="text-sm font-medium text-accent-700">Status</span>
                  </div>
                  <span className={getStatusBadge(selectedAgent)}>{getStatusText(selectedAgent)}</span>
                </div>

                {/* Details */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-3 bg-accent-50 rounded-lg">
                    <Mail className="w-5 h-5 text-accent-600" />
                    <div>
                      <div className="text-sm font-medium text-accent-700">Email</div>
                      <div className="text-accent-900">{selectedAgent.user.email}</div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 p-3 bg-accent-50 rounded-lg">
                    <Building className="w-5 h-5 text-accent-600" />
                    <div>
                      <div className="text-sm font-medium text-accent-700">Team</div>
                      <div className="text-accent-900">
                        {teamsLoading ? (
                          <span className="text-accent-400">Loading...</span>
                        ) : (
                          teams.find((t) => t.id === selectedAgent.teamId)?.name || "Unassigned"
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 p-3 bg-accent-50 rounded-lg">
                    <Calendar className="w-5 h-5 text-accent-600" />
                    <div>
                      <div className="text-sm font-medium text-accent-700">Created</div>
                      <div className="text-accent-900">{new Date(selectedAgent.createdAt).toLocaleDateString()}</div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 p-3 bg-accent-50 rounded-lg">
                    <Users className="w-5 h-5 text-accent-600" />
                    <div>
                      <div className="text-sm font-medium text-accent-700">Licensed Agent ID</div>
                      <div className="text-accent-900 font-mono text-sm">{selectedAgent.agentCode}</div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="border-t border-accent-200 pt-6">
                  <h4 className="text-lg font-medium text-accent-900 mb-4">Quick Actions</h4>
                  <div className="flex flex-wrap gap-3">
                    {selectedAgent.status !== "active" && (
                      <button
                        onClick={() => {
                          setShowAgentDetailsModal(false)
                          handleStatusChange(selectedAgent, "activate")
                        }}
                        disabled={loadingAgentId === selectedAgent.id}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingAgentId === selectedAgent.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <UserCheck className="w-4 h-4" />
                        )}
                        <span>Activate</span>
                      </button>
                    )}
                    {selectedAgent.status === "active" && (
                      <button
                        onClick={() => {
                          setShowAgentDetailsModal(false)
                          handleStatusChange(selectedAgent, "deactivate")
                        }}
                        disabled={loadingAgentId === selectedAgent.id}
                        className="flex items-center space-x-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingAgentId === selectedAgent.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <UserX className="w-4 h-4" />
                        )}
                        <span>Deactivate</span>
                      </button>
                    )}
                    {selectedAgent.status === "active" && (
                      <button
                        onClick={() => {
                          setShowAgentDetailsModal(false)
                          handleStatusChange(selectedAgent, "terminate")
                        }}
                        disabled={loadingAgentId === selectedAgent.id}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingAgentId === selectedAgent.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <AlertTriangle className="w-4 h-4" />
                        )}
                        <span>Terminate</span>
                      </button>
                    )}
                    <button 
                      onClick={() => handleEditAgent(selectedAgent)}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                      <span>Edit Details</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Confirmation Modal */}
      {showConfirmModal && selectedAgent && actionType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  actionType === "activate"
                    ? "bg-green-100"
                    : actionType === "deactivate"
                      ? "bg-orange-100"
                      : "bg-red-100"
                }`}
              >
                {actionType === "activate" ? (
                  <UserCheck className="w-5 h-5 text-green-600" />
                ) : actionType === "deactivate" ? (
                  <UserX className="w-5 h-5 text-orange-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-accent-900">Confirm Action</h3>
            </div>

            <p className="text-accent-600 mb-6">
              Are you sure you want to {getActionText()}?
              {actionType === "deactivate" && " The licensed agent will be temporarily suspended and can be reactivated later."}
              {actionType === "terminate" && " This will permanently remove the licensed agent from all teams and cannot be undone."}
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false)
                  setSelectedAgent(null)
                  setActionType(null)
                  setStatusChangeLoading(false)
                  setLoadingAgentId(null)
                }}
                disabled={statusChangeLoading}
                className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                disabled={statusChangeLoading}
                className={`font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2 ${
                  actionType === "activate"
                    ? "bg-green-600 hover:bg-green-700 text-white disabled:bg-green-400"
                    : actionType === "deactivate"
                      ? "bg-orange-600 hover:bg-orange-700 text-white disabled:bg-orange-400"
                      : "bg-red-600 hover:bg-red-700 text-white disabled:bg-red-400"
                }`}
              >
                {statusChangeLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>
                  {statusChangeLoading 
                    ? "Processing..." 
                    : actionType === "activate" 
                      ? "Activate" 
                      : actionType === "deactivate" 
                        ? "Deactivate" 
                        : "Terminate"
                  }
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedAgentForReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <Key className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Reset Password</h2>
                  <p className="text-blue-100 text-sm">Generate new temporary password</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowResetPasswordModal(false)
                  setSelectedAgentForReset(null)
                  setResetPasswordSuccess(false)
                  setNewTemporaryPassword("")
                  setResetPasswordError(null)
                  setPasswordCopied(false)
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {!resetPasswordSuccess ? (
                <>
                  {/* Confirmation Message */}
                  <div className="flex items-start space-x-3 mb-6">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Key className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">Reset Licensed Agent Password</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Are you sure you want to reset the password for:
                      </p>
                    </div>
                  </div>

                  {/* Agent Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-bold text-lg">
                          {selectedAgentForReset.user.firstName?.[0]}{selectedAgentForReset.user.lastName?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {selectedAgentForReset.user.firstName} {selectedAgentForReset.user.lastName}
                        </p>
                        <p className="text-sm text-gray-600">{selectedAgentForReset.user.email}</p>
                        <p className="text-xs text-gray-500">Licensed Agent Code: {selectedAgentForReset.agentCode}</p>
                      </div>
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-yellow-800">
                        <p className="font-medium mb-1">Important:</p>
                        <ul className="list-disc ml-4 space-y-1">
                          <li>A new temporary password will be generated</li>
                          <li>The agent will be notified via email</li>
                          <li>Agent must change password on next login</li>
                          <li>Current password will no longer work</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Error Display */}
                  {resetPasswordError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <p className="text-sm text-red-800">{resetPasswordError}</p>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowResetPasswordModal(false)
                        setSelectedAgentForReset(null)
                        setResetPasswordError(null)
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                      disabled={resettingPassword}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleResetPassword(selectedAgentForReset.id)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      disabled={resettingPassword}
                    >
                      {resettingPassword ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Resetting...</span>
                        </>
                      ) : (
                        <>
                          <Key className="w-4 h-4" />
                          <span>Reset Password</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Success State */}
                  <div className="text-center py-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Password Reset Successful!</h3>
                    <p className="text-sm text-gray-600 mb-6">
                      A new temporary password has been generated and sent to the licensed agent's email.
                    </p>

                    {/* Temporary Password Display */}
                    <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 mb-6">
                      <p className="text-xs text-gray-600 mb-2">Temporary Password:</p>
                      <div className="flex items-center justify-center space-x-2">
                        <code className="text-lg font-mono font-bold text-gray-900 bg-white px-4 py-2 rounded border border-gray-300">
                          {newTemporaryPassword}
                        </code>
                        <button
                          onClick={handleCopyPassword}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Copy to clipboard"
                        >
                          {passwordCopied ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <Copy className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        ⚠️ Save this password securely. Agent must change it on first login.
                      </p>
                    </div>

                    {/* Agent Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                      <p className="text-xs text-blue-800">
                        <strong>Email sent to:</strong> {selectedAgentForReset.user.email}
                      </p>
                    </div>

                    <button
                      onClick={() => {
                        setShowResetPasswordModal(false)
                        setSelectedAgentForReset(null)
                        setResetPasswordSuccess(false)
                        setNewTemporaryPassword("")
                        setPasswordCopied(false)
                      }}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Agent Modal */}
      {showEditAgentModal && editingAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Edit className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-accent-900">Edit Licensed Agent Details</h3>
                </div>
                <button
                  onClick={() => {
                    setShowEditAgentModal(false)
                    setEditingAgent(null)
                    setAgentUpdateError("")
                  }}
                  className="text-accent-400 hover:text-accent-600 p-1 rounded-lg hover:bg-accent-100"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Error Display */}
              {agentUpdateError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                    <p className="text-red-800 font-medium">{agentUpdateError}</p>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h4 className="text-lg font-medium text-accent-900 mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="input-field"
                        value={editAgentData.firstName}
                        onChange={(e) => setEditAgentData({ ...editAgentData, firstName: e.target.value })}
                        placeholder="Enter first name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="input-field"
                        value={editAgentData.lastName}
                        onChange={(e) => setEditAgentData({ ...editAgentData, lastName: e.target.value })}
                        placeholder="Enter last name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
                        <input
                          type="email"
                          className="input-field pl-10"
                          value={editAgentData.email}
                          onChange={(e) => setEditAgentData({ ...editAgentData, email: e.target.value })}
                          placeholder="salesrep@company.com"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">Phone Number</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
                        <input
                          type="tel"
                          className="input-field pl-10"
                          value={editAgentData.phone}
                          onChange={(e) => {
                            const formatted = formatPhone(e.target.value);
                            setEditAgentData({ ...editAgentData, phone: formatted });
                          }}
                          placeholder="(123) 456-7890"
                          maxLength={14}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Manager Information */}
                <div>
                  <h4 className="text-lg font-medium text-accent-900 mb-4 flex items-center">
                    <UserIcon className="w-5 h-5 mr-2" />
                    Manager Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">Manager Email</label>
                      <select
                        className="input-field mb-2"
                        value={editAgentData.managerEmailSource}
                        onChange={(e) => setEditAgentData({ ...editAgentData, managerEmailSource: e.target.value })}
                      >
                        <option value="auto">Auto Inherit (Recruiter's Email)</option>
                        <option value="manual">Enter Manually</option>
                      </select>
                      {editAgentData.managerEmailSource === "manual" && (
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
                          <input
                            type="email"
                            className="input-field pl-10"
                            value={editAgentData.managerEmail}
                            onChange={(e) => setEditAgentData({ ...editAgentData, managerEmail: e.target.value })}
                            placeholder="manager@company.com"
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">Manager Name</label>
                      <select
                        className="input-field mb-2"
                        value={editAgentData.managerNameSource}
                        onChange={(e) => setEditAgentData({ ...editAgentData, managerNameSource: e.target.value })}
                      >
                        <option value="auto">Auto Inherit (Recruiter's Name)</option>
                        <option value="manual">Enter Manually</option>
                      </select>
                      {editAgentData.managerNameSource === "manual" && (
                        <div className="relative">
                          <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
                          <input
                            type="text"
                            className="input-field pl-10"
                            value={editAgentData.managerName}
                            onChange={(e) => setEditAgentData({ ...editAgentData, managerName: e.target.value })}
                            placeholder="Manager Full Name"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Work Information */}
                <div>
                  <h4 className="text-lg font-medium text-accent-900 mb-4 flex items-center">
                    <Building className="w-5 h-5 mr-2" />
                    Work Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">
                        Start Date <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
                        <input
                          type="date"
                          className="input-field pl-10"
                          value={editAgentData.startDate}
                          onChange={(e) => setEditAgentData({ ...editAgentData, startDate: e.target.value })}
                          required
                          disabled
                        />
                      </div>
                      <p className="text-xs text-accent-500 mt-1">Start date cannot be modified</p>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div>
                  <h4 className="text-lg font-medium text-accent-900 mb-4 flex items-center">
                    <MapPin className="w-5 h-5 mr-2" />
                    Additional Information
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-accent-700 mb-2">Address</label>
                      <textarea
                        className="input-field"
                        rows={2}
                        value={editAgentData.address}
                        onChange={(e) => setEditAgentData({ ...editAgentData, address: e.target.value })}
                        placeholder="Enter full address"
                        disabled
                      />
                      <p className="text-xs text-accent-500 mt-1">Address editing not available</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-accent-700 mb-2">Emergency Contact Name</label>
                        <input
                          type="text"
                          className="input-field"
                          value={editAgentData.emergencyContact}
                          onChange={(e) => setEditAgentData({ ...editAgentData, emergencyContact: e.target.value })}
                          placeholder="Contact person name"
                          disabled
                        />
                        <p className="text-xs text-accent-500 mt-1">Emergency contact editing not available</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-accent-700 mb-2">
                          Emergency Contact Phone
                        </label>
                        <input
                          type="tel"
                          className="input-field"
                          value={editAgentData.emergencyPhone}
                          onChange={(e) => {
                            const formatted = formatPhone(e.target.value);
                            setEditAgentData({ ...editAgentData, emergencyPhone: formatted });
                          }}
                          placeholder="(123) 456-7890"
                          maxLength={14}
                          disabled
                        />
                        <p className="text-xs text-accent-500 mt-1">Emergency phone editing not available</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-accent-200">
                <button 
                  onClick={() => {
                    setShowEditAgentModal(false)
                    setEditingAgent(null)
                    setAgentUpdateError("")
                  }} 
                  className="btn-outline" 
                  disabled={updatingAgent}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateAgent}
                  disabled={updatingAgent}
                  className="btn-primary flex items-center space-x-2 bg-blue-600 hover:bg-blue-700"
                >
                  {updatingAgent ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Updating Licensed Agent...</span>
                    </>
                  ) : (
                    <>
                      <Edit className="w-4 h-4" />
                      <span>Update Licensed Agent</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
